import { inject, Injectable } from '@angular/core';
import {
  PerformanceMetric,
  PerformanceMetricCategory,
  AppStartMetricAttributes,
  ScreenRenderMetricAttributes,
  ViewRenderMetricAttributes,
  AppStartEvent,
  ScreenName,
  ScreenTrackingConfig,
  ScreenTrackingState,
  PerformanceMetricBaseAttributes,
  ImageLoadMetricPayload,
  ImageLoadStartPayload,
  ImageLoadCompletePayload,
  NetworkRequestStartPayload,
  NetworkRequestCompletePayload,
  WebVitalsMetricAttributes,
} from './performance-metrics.interfaces';
import { OTWebVitals } from '@lentau/core/open-telemetry/web-vitals/shared/ot-webvitals.interfaces';
import { Router } from '@angular/router';
import { NAVIGATOR } from '@lentau/ui-kit/core/tokens/navigator';
import { environment } from '@lentau/core/environments';
import { ScreenConfigs } from './performance-metrics.config';
import { PerformanceMetricsSpanManager, type MetricSpanResult } from './performance-metrics-span.manager';
import { DeviceIdService } from '@lentau/core/api/shared/device-id.service';
import { PerformanceMetricsBufferService } from './performance-metrics-buffer.service';
import { CustomIdGenerator } from '../open-telemetry.span';
import { WINDOW } from '@lentau/ui-kit/core/tokens/window';

@Injectable({
  providedIn: 'root',
})
export class PerformanceMetricsService {
  private readonly spanManager: PerformanceMetricsSpanManager = inject(PerformanceMetricsSpanManager);
  private readonly bufferService: PerformanceMetricsBufferService = inject(PerformanceMetricsBufferService);
  private readonly deviceIdService: DeviceIdService = inject(DeviceIdService);
  private readonly router: Router = inject(Router);
  private readonly navigator: Navigator = inject(NAVIGATOR);
  private readonly window: Window = inject(WINDOW);
  private readonly idGenerator: CustomIdGenerator = new CustomIdGenerator();

  private appStartTime: number = performance.now();

  private screenTrackingStates: Map<string, ScreenTrackingState> = new Map<string, ScreenTrackingState>();
  private imageTrackingIds: Map<string, string> = new Map();
  private networkTrackingIds: Map<string, string> = new Map();
  private currentRegionName: string | null = null;
  private currentRegionSlug: string | null = null;

  private getBaseAttributes(duration: number): PerformanceMetricBaseAttributes {
    return {
      timestamp: Date.now(),
      device_id: this.deviceIdService.getDeviceId(),
      platform: 'Web',
      version: environment.releaseVersion || 'unknown',
      duration,
    };
  }

  private getBaseAttributesWithCast<T extends PerformanceMetricBaseAttributes>(duration: number): T {
    return this.getBaseAttributes(duration) as T;
  }

  private ensureParentSpanExists(): Promise<void> {
    if (!this.spanManager.hasParentReference()) {
      return this.trackStart();
    }
    return Promise.resolve();
  }

  private generateTrackingId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  public async trackStart(): Promise<void> {
    this.spanManager.setBufferService(this.bufferService);
    this.spanManager.initializeAppStartIds(this.appStartTime);
    const attributes: AppStartMetricAttributes = this.createAppStartAttributes();
    const metric: PerformanceMetric = this.createAppStartMetric(attributes);
    await this.spanManager.beginAppStart(metric);
  }

  private createAppStartAttributes(): AppStartMetricAttributes {
    return {
      ...this.getBaseAttributesWithCast<AppStartMetricAttributes>(0),
      start_type: AppStartEvent.COLD_START,
    };
  }

  private createAppStartMetric(attributes: AppStartMetricAttributes): PerformanceMetric {
    return {
      category: PerformanceMetricCategory.APP_START,
      event: AppStartEvent.COLD_START,
      attributes,
    };
  }

  public startScreenTracking(screenName: ScreenName | string): void {
    void this.initializeScreenTracking(screenName);
  }

  private async initializeScreenTracking(screenName: ScreenName | string): Promise<void> {
    const config: ScreenTrackingConfig | undefined = ScreenConfigs.get(screenName);
    if (!config) {
      console.error('[Performance Metrics]: no configuration for screen', screenName);
      this.screenTrackingStates.delete(screenName);
      return;
    }

    const normalizedScreenName: string = screenName as string;
    this.setupScreenSpan(normalizedScreenName);
    const state: ScreenTrackingState = this.createScreenTrackingState();
    this.screenTrackingStates.set(screenName, state);

    const metric: PerformanceMetric = this.createScreenRenderMetric(screenName);
    const result: MetricSpanResult = await this.spanManager.beginScreen(normalizedScreenName, metric);
    state.traceId = result.traceId;
  }

  private setupScreenSpan(normalizedScreenName: string): void {
    this.spanManager.setCurrentScreenName(normalizedScreenName);
    this.spanManager.initializeScreenSpanId(normalizedScreenName);
  }

  private createScreenTrackingState(): ScreenTrackingState {
    return {
      startTime: performance.now(),
      matchedBlockingRequests: new Map<string, string>(),
      isReady: false,
    };
  }

  private createScreenRenderMetric(screenName: ScreenName | string): PerformanceMetric {
    return {
      category: PerformanceMetricCategory.SCREEN_RENDER,
      event: screenName as string,
      attributes: {
        ...this.getBaseAttributesWithCast<ScreenRenderMetricAttributes>(0),
        screen_name: screenName,
      },
    };
  }

  public handleBlockingRequestCompletion(requestUrl: string): void {
    ScreenConfigs.forEach((_config: ScreenTrackingConfig, screen: ScreenName | string) => {
      void this.markRequestCompleted(screen, requestUrl);
    });
  }

  public async markRequestCompleted(screenName: ScreenName | string, requestUrl: string): Promise<void> {
    const state: ScreenTrackingState | undefined = this.screenTrackingStates.get(screenName);
    const config: ScreenTrackingConfig | undefined = ScreenConfigs.get(screenName);

    if (!state || !config || state.isReady) {
      return;
    }

    const matchedPattern: string | undefined = this.findMatchingPattern(config, requestUrl);
    if (!matchedPattern) {
      return;
    }

    state.matchedBlockingRequests.set(matchedPattern, requestUrl);

    if (this.areAllRequestsCompleted(config, state)) {
      await this.finalizeScreenTracking(screenName, state, config);
    }
  }

  private findMatchingPattern(config: ScreenTrackingConfig, requestUrl: string): string | undefined {
    return config.blockingRequests.find((url: string) => requestUrl.toLowerCase().includes(url.toLowerCase()));
  }

  private areAllRequestsCompleted(config: ScreenTrackingConfig, state: ScreenTrackingState): boolean {
    return config.blockingRequests.every((url: string) => state.matchedBlockingRequests.has(url));
  }

  private async finalizeScreenTracking(
    screenName: ScreenName | string,
    state: ScreenTrackingState,
    config: ScreenTrackingConfig
  ): Promise<void> {
    state.isReady = true;
    const blockingRequests: string[] = config.blockingRequests.map(
      (url: string) => state.matchedBlockingRequests.get(url) ?? url
    );
    await this.trackScreenRender(screenName, state, blockingRequests);
  }

  private async trackScreenRender(
    screenName: ScreenName | string,
    state: ScreenTrackingState,
    blockingRequests: string[]
  ): Promise<void> {
    const duration: number = performance.now() - state.startTime;
    const traceId: string | undefined = state.traceId;
    const attributes: ScreenRenderMetricAttributes = this.createScreenRenderCompleteAttributes(
      screenName,
      duration,
      blockingRequests,
      traceId
    );
    this.markAppStable();

    await this.spanManager.finalizeScreen(
      screenName as string,
      {
        category: PerformanceMetricCategory.SCREEN_RENDER,
        event: screenName as string,
        attributes,
      },
      {
        duration,
        blockingRequestCount: blockingRequests.length,
      }
    );

    this.screenTrackingStates.delete(screenName);
  }

  private createScreenRenderCompleteAttributes(
    screenName: ScreenName | string,
    duration: number,
    blockingRequests: string[],
    traceId?: string
  ): ScreenRenderMetricAttributes {
    return {
      ...this.getBaseAttributesWithCast<ScreenRenderMetricAttributes>(duration),
      screen_name: screenName,
      blocking_requests: blockingRequests,
      trace_id: traceId,
    };
  }

  public async trackViewRender(viewName: string, startTime: number, parentScreen?: ScreenName | string): Promise<void> {
    const duration: number = performance.now() - startTime;
    const traceId: string | undefined = await this.spanManager.getActiveTraceId();
    const attributes: ViewRenderMetricAttributes = this.createViewRenderAttributes(
      viewName,
      duration,
      parentScreen,
      traceId ?? ''
    );

    await this.spanManager.recordChildMetric({
      category: PerformanceMetricCategory.VIEW_RENDER,
      event: viewName,
      attributes,
    });
  }

  private createViewRenderAttributes(
    viewName: string,
    duration: number,
    parentScreen: ScreenName | string | undefined,
    traceId: string | undefined
  ): ViewRenderMetricAttributes {
    return {
      ...this.getBaseAttributesWithCast<ViewRenderMetricAttributes>(duration),
      view_name: viewName,
      parent_screen: parentScreen,
      trace_id: traceId,
    };
  }

  public async trackNetworkRequest(
    requestUrl: string,
    httpMethod: string,
    responseCode: number,
    duration: number,
    requestSize?: number,
    responseSize?: number,
    traceId?: string
  ): Promise<void> {
    const trackingId: string = this.getNetworkTrackingId(requestUrl);
    await this.startNetworkTracking(requestUrl, httpMethod, requestSize);
    await this.completeNetworkTracking(trackingId, {
      responseCode,
      responseSize,
      duration,
      traceId,
    });
  }

  public async startNetworkTracking(
    requestUrl: string,
    httpMethod: string,
    requestSize?: number,
    startTime?: number
  ): Promise<string> {
    await this.ensureParentSpanExists();

    const trackingId: string = this.getNetworkTrackingId(requestUrl);
    const payload: NetworkRequestStartPayload = this.createNetworkRequestStartPayload(
      requestUrl,
      httpMethod,
      requestSize,
      startTime
    );

    await this.bufferService.startNetworkTracking(trackingId, payload);
    return trackingId;
  }

  private createNetworkRequestStartPayload(
    requestUrl: string,
    httpMethod: string,
    requestSize: number | undefined,
    startTime: number | undefined
  ): NetworkRequestStartPayload {
    return {
      requestUrl,
      httpMethod,
      requestSize,
      startTime,
    };
  }

  public async completeNetworkTracking(
    trackingId: string,
    completePayload: NetworkRequestCompletePayload
  ): Promise<void> {
    await this.bufferService.completeNetworkTracking(trackingId, completePayload);
    this.networkTrackingIds.delete(trackingId);
  }

  private getNetworkTrackingId(requestUrl: string): string {
    let trackingId: string | undefined = this.networkTrackingIds.get(requestUrl);
    if (!trackingId) {
      trackingId = this.generateTrackingId('network');
      this.networkTrackingIds.set(requestUrl, trackingId);
    }
    return trackingId;
  }

  public async trackImageLoadMetric(payload: ImageLoadMetricPayload): Promise<void> {
    const trackingId: string = this.getImageTrackingId(payload.componentName, payload.requestedSrc);
    const startPayload: ImageLoadStartPayload = this.createImageLoadStartPayload(payload);

    await this.startImageTracking(startPayload);
    await this.completeImageTracking(trackingId, this.createImageLoadCompletePayload(payload));
  }

  public setCurrentRegion(regionName: string | null, regionSlug: string | null): void {
    this.currentRegionName = regionName;
    this.currentRegionSlug = regionSlug;
  }

  private createImageLoadStartPayload(payload: ImageLoadMetricPayload): ImageLoadStartPayload {
    const currentScreen: string | null = this.spanManager.getCurrentScreenName();
    return {
      componentName: payload.componentName,
      requestedSrc: payload.requestedSrc,
      renderedSrc: payload.renderedSrc,
      width: payload.width,
      height: payload.height,
      priority: payload.priority,
      imageCategory: payload.imageCategory,
      qaId: payload.qaId,
      alt: payload.alt,
      screen: payload.screen ?? currentScreen ?? undefined,
      regionSlug: this.currentRegionSlug ?? undefined,
    };
  }

  private createImageLoadCompletePayload(payload: ImageLoadMetricPayload): ImageLoadCompletePayload {
    return {
      status: payload.status,
      duration: payload.duration,
      naturalWidth: payload.naturalWidth,
      naturalHeight: payload.naturalHeight,
      displayedWidth: payload.displayedWidth,
      displayedHeight: payload.displayedHeight,
      currentSrc: payload.currentSrc,
      regionName: this.currentRegionName ?? undefined,
      errorMessage: payload.errorMessage,
    };
  }

  public async startImageTracking(payload: ImageLoadStartPayload): Promise<string> {
    await this.ensureParentSpanExists();

    const trackingId: string = this.getImageTrackingId(payload.componentName, payload.requestedSrc);
    await this.bufferService.startImageTracking(trackingId, payload);
    return trackingId;
  }

  public async completeImageTracking(trackingId: string, completePayload: ImageLoadCompletePayload): Promise<void> {
    await this.bufferService.completeImageTracking(trackingId, completePayload);
    this.imageTrackingIds.delete(trackingId);
  }

  private getImageTrackingId(componentName: string, requestedSrc: string): string {
    const key: string = `${componentName}_${requestedSrc}`;
    let trackingId: string | undefined = this.imageTrackingIds.get(key);
    if (!trackingId) {
      trackingId = this.generateTrackingId('image');
      this.imageTrackingIds.set(key, trackingId);
    }
    return trackingId;
  }

  public markAppStable(): void {
    const duration: number = performance.now() - this.appStartTime;
    const attributes: AppStartMetricAttributes = this.createAppStableAttributes(duration);
    this.spanManager.updateAppStartAttributes(attributes);
  }

  private createAppStableAttributes(duration: number): AppStartMetricAttributes {
    return {
      ...this.getBaseAttributesWithCast<AppStartMetricAttributes>(duration),
      start_type: AppStartEvent.COLD_START,
    };
  }

  public async trackWebVitals(webVitals: OTWebVitals): Promise<void> {
    const attributes: WebVitalsMetricAttributes = this.createWebVitalsAttributes(webVitals);

    const traceId: string | undefined = attributes['web_vitals.trace_id'];

    await this.spanManager.recordRootMetric(
      {
        category: PerformanceMetricCategory.WEB_VITALS,
        event: 'web_vitals',
        attributes,
      },
      { traceId }
    );
  }

  private createWebVitalsAttributes(webVitals: OTWebVitals): WebVitalsMetricAttributes {
    const eventId: string = webVitals.eventId || this.idGenerator.generateSpanId();

    const pageUrl: string = this.router.url || this.window.location.pathname || '/';

    return {
      ...this.getBaseAttributesWithCast<WebVitalsMetricAttributes>(0),
      page_url: pageUrl,
      user_device: this.navigator.userAgent,
      'webvital.cls.raw': webVitals.cls,
      'webvital.cls.rating': this.getRating(webVitals.cls, [0.1, 0.25]),
      'webvital.fcp.raw': webVitals.fcp,
      'webvital.fcp.rating': this.getRating(webVitals.fcp, [1800, 3000]),
      'webvital.lcp.raw': webVitals.lcp,
      'webvital.lcp.rating': this.getRating(webVitals.lcp, [2500, 4000]),
      'webvital.inp.raw': webVitals.inp,
      'webvital.inp.rating': webVitals.inp ? this.getRating(webVitals.inp, [200, 500]) : undefined,
      'webvital.ttfb.raw': webVitals.ttfb,
      'webvital.ttfb.rating': this.getRating(webVitals.ttfb, [800, 1800]),
      'web_vitals.event_id': eventId,
      'web_vitals.trace_id': webVitals.traceId,
      'web_vitals.release': environment.releaseVersion,
    };
  }

  private getRating(value: number, thresholds: [number, number]): string {
    return value <= thresholds[0] ? 'good' : value <= thresholds[1] ? 'needs-improvement' : 'poor';
  }
}
